import { formatMoney } from "utils";

class QuickOrderList extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.submitButton = this.querySelector('[type="submit"]');
    this.clearButton = this.querySelector("[data-quick-order-clear]");
    this.itemsTotal = this.querySelector("[data-quick-order-items]");
    this.subtotal = this.querySelector("[data-quick-order-subtotal]");
    this.status = this.querySelector("[data-quick-order-status]");
    this.inputs = [...this.querySelectorAll("input[data-variant-id]")];
    this.rowClearButtons = [...this.querySelectorAll("[data-quick-order-row-clear]")];
    this.onSubmit = this.handleSubmit.bind(this);
    this.onQuantityChange = () => this.updateSummary();
    this.onClear = this.clearQuantities.bind(this);
    this.onRowClear = this.clearRowQuantity.bind(this);

    this.form.addEventListener("submit", this.onSubmit);
    this.clearButton.addEventListener("click", this.onClear);
    this.rowClearButtons.forEach((button) => {
      button.addEventListener("click", this.onRowClear);
    });
    this.inputs.forEach((input) => {
      input.addEventListener("change", this.onQuantityChange);
      input.closest("quantity-input")?.addEventListener("update", this.onQuantityChange);
    });
    this.updateSummary();
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmit);
    this.clearButton?.removeEventListener("click", this.onClear);
    this.rowClearButtons?.forEach((button) => {
      button.removeEventListener("click", this.onRowClear);
    });
    this.inputs?.forEach((input) => {
      input.removeEventListener("change", this.onQuantityChange);
      input.closest("quantity-input")?.removeEventListener("update", this.onQuantityChange);
    });
  }

  getSelectedItems() {
    return this.inputs
      .map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: Math.max(0, Number(input.value || 0)),
        price: Number(input.dataset.variantPrice || 0),
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  updateSummary(clearStatus = true) {
    let totalItems = 0;
    let subtotal = 0;

    this.inputs.forEach((input) => {
      const quantity = Math.max(0, Number(input.value || 0));
      const price = Number(input.dataset.variantPrice || 0);
      const rowTotal = quantity * price;
      const totalCell = input.closest("tr")?.querySelector("[data-variant-total] .money");

      if (totalCell) {
        totalCell.innerHTML = this.formatMoney(rowTotal);
      }

      totalItems += quantity;
      subtotal += rowTotal;
    });

    this.submitButton.disabled = totalItems === 0;
    this.clearButton.hidden = totalItems === 0;
    this.itemsTotal.textContent = totalItems;
    this.subtotal.innerHTML = this.formatMoney(subtotal);

    if (clearStatus) {
      this.setStatus("");
    }
  }

  formatMoney(value) {
    return formatMoney(value, this.dataset.moneyFormat || window.theme.money_format);
  }

  setStatus(message, isError = false) {
    this.status.textContent = message;
    if (isError) {
      this.status.setAttribute("aria-invalid", "true");
    } else {
      this.status.removeAttribute("aria-invalid");
    }
  }

  clearQuantities() {
    this.inputs.forEach((input) => {
      input.value = 0;
    });
    this.updateSummary();
  }

  clearRowQuantity(event) {
    const input = event.currentTarget.closest("tr")?.querySelector("input[data-variant-id]");
    if (!input) return;
    input.value = 0;
    this.updateSummary();
  }

  async handleSubmit(event) {
    event.preventDefault();

    const items = this.getSelectedItems().map(({ id, quantity }) => ({ id, quantity }));
    if (!items.length) {
      this.setStatus(this.dataset.emptyMessage, true);
      return;
    }

    this.submitButton.disabled = true;
    this.submitButton.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.description || this.dataset.errorMessage);
      }

      if (this.hasAttribute("data-redirect-to-cart")) {
        window.location.href = this.dataset.cartUrl;
        return;
      }

      this.clearQuantities();
      this.updateSummary(false);
      this.setStatus(this.dataset.successMessage);
    } catch (error) {
      this.setStatus(error.message || this.dataset.errorMessage, true);
    } finally {
      this.submitButton.removeAttribute("aria-busy");
      this.updateSummary(false);
    }
  }
}

if (!customElements.get("quick-order-list")) {
  customElements.define("quick-order-list", QuickOrderList);
}
